import { useState } from 'react';
import { ShoppingBasket, ChevronUp, ChevronDown, Check } from 'lucide-react';

const money = (n) => {
  if (n == null || isNaN(n)) return '£0';
  const v = Number(n);
  if (v === Math.floor(v)) return `£${v.toLocaleString()}`;
  return `£${v.toFixed(2)}`;
};

/* Inline compact summary line */
function Row({ label, sub, price, muted }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 dark:border-white/5 last:border-b-0">
      <Check className="w-3.5 h-3.5 text-[#00E5FF] mt-1 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className={`text-xs ${muted ? 'text-slate-500 dark:text-slate-500' : 'text-[#0A0A0A] dark:text-white'} font-medium truncate`}>{label}</p>
        {sub && <p className="text-[10px] text-slate-500 dark:text-slate-500 truncate">{sub}</p>}
      </div>
      <span className="text-xs font-mono text-slate-400 dark:text-slate-300 font-semibold flex-shrink-0">{price}</span>
    </div>
  );
}

/* Main basket content — used in sidebar (desktop) and sheet (mobile) */
function BasketContents({ state }) {
  const tier = state.tier;
  const boltOns = state.boltOns || [];
  const boosts = state.boosts || {};
  const care = state.carePlan;
  const host = state.hosting;
  const monthlyBoosts = Object.entries(boosts);
  return (
    <>
      <div className="space-y-0.5">
        {tier
          ? <Row label={tier.name} sub={tier.tagline} price={money(tier.price)} />
          : <p className="text-xs text-slate-500 dark:text-slate-500 py-3 text-center italic">Pick your foundation to begin</p>
        }
        {boltOns.map(b => (
          <Row key={b.id} label={b.name} price={`${money(b.price)}${b.price_suffix || ''}`} />
        ))}
      </div>

      {(care || host || monthlyBoosts.length > 0) && (
        <>
          <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent my-3" />
          <p className="text-[9px] uppercase tracking-widest text-slate-400 dark:text-slate-600 mb-1">Monthly recurring</p>
          <div className="space-y-0.5">
            {care && <Row label={care.name} price={`${money(care.price_monthly)}/mo`} />}
            {host && !state.skipHosting && host.monthly_surcharge > 0 && (
              <Row label={`${host.name} Hosting`} sub={host.ram} price={`+${money(host.monthly_surcharge)}/mo`} />
            )}
            {monthlyBoosts.map(([boltId, pack]) => {
              const bolt = boltOns.find(b => b.id === boltId);
              if (!bolt || !pack) return null;
              return (
                <Row
                  key={`boost-${boltId}`}
                  label={`${bolt.name} · ${pack.name}`}
                  sub={pack.credits}
                  price={`+${money(pack.price_monthly)}/mo`}
                />
              );
            })}
            {state.skipHosting && !care && <Row label="Hosting skipped" muted sub="No admin / backups / security" price="£0" />}
          </div>
        </>
      )}
    </>
  );
}

function Totals({ setupTotal, monthlyTotal }) {
  return (
    <div className="mt-4 rounded-xl overflow-hidden">
      <div className="bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-4 py-3 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-slate-400 dark:text-slate-400">One-time setup</span>
        <span className="font-['Outfit'] text-lg font-black text-[#0A0A0A] dark:text-white">{money(setupTotal)}</span>
      </div>
      <div className="bg-violet-500/10 border border-violet-500/20 border-t-0 px-4 py-3 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-slate-400 dark:text-slate-400">Monthly</span>
        <span className="font-['Outfit'] text-lg font-black text-[#0A0A0A] dark:text-white">{money(monthlyTotal)}<span className="text-xs text-slate-500 dark:text-slate-500 font-normal">/mo</span></span>
      </div>
    </div>
  );
}

export default function LiveBasket({ state, setupTotal, monthlyTotal, pulseKey }) {
  const [openMobile, setOpenMobile] = useState(false);
  const itemCount = (state.tier ? 1 : 0) + (state.boltOns?.length || 0) + (state.carePlan ? 1 : 0) + (state.hosting && !state.skipHosting ? 1 : 0) + Object.keys(state.boosts || {}).length;

  return (
    <>
      {/* DESKTOP — sticky sidebar */}
      <aside data-testid="live-basket-desktop" className="hidden lg:block sticky top-24 self-start">
        <div className="relative rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] backdrop-blur-xl p-5 shadow-[0_8px_32px_rgba(0,229,255,0.04)] overflow-hidden">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#00E5FF]/10 blur-[60px] pointer-events-none" />
          <div className="relative flex items-center gap-2 mb-4">
            <div key={pulseKey} className="relative w-8 h-8 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center">
              <ShoppingBasket className="w-4 h-4 text-[#00E5FF]" />
              {pulseKey > 0 && (
                <span className="absolute inset-0 rounded-lg border-2 border-[#00E5FF] animate-[ping_0.6s_ease-out_forwards]" />
              )}
            </div>
            <h3 className="font-['Outfit'] text-sm font-bold text-[#0A0A0A] dark:text-white">Your Basket</h3>
            <span className="ml-auto text-[10px] text-slate-500 dark:text-slate-500 font-mono">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
          </div>
          <div className="relative">
            <BasketContents state={state} />
            <Totals setupTotal={setupTotal} monthlyTotal={monthlyTotal} />
          </div>
        </div>
      </aside>

      {/* MOBILE — bottom dock */}
      <div data-testid="live-basket-mobile" className="lg:hidden fixed bottom-0 inset-x-0 z-40 pointer-events-none">
        <div className="pointer-events-auto bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-slate-200 dark:border-white/10 shadow-[0_-4px_32px_rgba(0,0,0,0.8)]">
          <button
            type="button"
            data-testid="basket-toggle-mobile"
            onClick={() => setOpenMobile(v => !v)}
            className="w-full flex items-center gap-3 px-4 py-3 active:bg-slate-100 dark:bg-white/5 transition-colors"
          >
            <div key={pulseKey} className="relative w-9 h-9 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center flex-shrink-0">
              <ShoppingBasket className="w-4 h-4 text-[#00E5FF]" />
              {pulseKey > 0 && <span className="absolute inset-0 rounded-lg border-2 border-[#00E5FF] animate-[ping_0.6s_ease-out_forwards]" />}
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-500">Your basket</p>
              <p className="text-sm font-bold text-[#0A0A0A] dark:text-white truncate">{money(setupTotal)} <span className="text-slate-500 dark:text-slate-500 font-normal">· </span><span className="text-[#00E5FF]">{money(monthlyTotal)}/mo</span></p>
            </div>
            {openMobile ? <ChevronDown className="w-5 h-5 text-slate-400 dark:text-slate-400" /> : <ChevronUp className="w-5 h-5 text-slate-400 dark:text-slate-400" />}
          </button>
          {openMobile && (
            <div className="px-4 pb-4 max-h-[50vh] overflow-y-auto">
              <BasketContents state={state} />
              <Totals setupTotal={setupTotal} monthlyTotal={monthlyTotal} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export { money };
